<template>
  <div class="replica-studio">
    <!-- ============ H3 品牌组合 hero ============ -->
    <div class="h3-hero">
      <div class="hero-brand">
        <img src="/img/h3brand/h3-logo-combo-white.webp" class="hero-logo" alt="Hailuo AI x MiniMax" />
        <div class="hero-title">
          <div class="ht-main">🔥 爆款复刻引擎</div>
          <div class="ht-sub">换人 · 换景 · 换品 · 保留爆款钩子与节奏 — MiniMax H3 驱动</div>
        </div>
      </div>
      <div class="hero-chips">
        <span class="h-chip">🧩 多模态全能参考</span>
        <span class="h-chip">🎞 源视频节奏保留</span>
        <span class="h-chip">🖼 2K · 原生立体声</span>
        <span class="h-chip">⏱ 4-15s 直出</span>
      </div>
    </div>

    <!-- ============ ① 选爆款源视频 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>🔥 爆款复刻 · 第一步：选爆款源视频</span>
          <el-tag size="small" type="danger" effect="plain">换人 / 换景 / 换品 · 保留爆款节奏</el-tag>
        </div>
      </template>
      <div class="row">
        <el-upload :auto-upload="false" :show-file-list="false" accept="video/*" :on-change="f => uploadFile('video', f)" style="display:inline-block">
          <el-button size="small" type="primary" plain>📤 上传本机爆款视频</el-button>
        </el-upload>
        <el-select v-model="form.source_video_id" filterable clearable placeholder="或从素材库选择已下载/上传的爆款视频（自动截取前15秒）" style="width:520px">
          <el-option v-for="a in videoAssets" :key="a.id" :label="(a.title || a.file_name) + '  #' + a.id" :value="a.id" />
        </el-select>
        <span class="row-tip">源视频任意时长均可：系统自动截取前 {{ Math.min(form.duration, 15) }} 秒并转码为标准参考片段</span>
      </div>
      <!-- 常驻：时长规则引导（无论选什么源都可见） -->
      <div class="dur-guide">
        <span class="dg-title">📐 时长规则（H3 单次上限 15 秒）</span>
        <ul>
          <li><b>≤15 秒</b>：直接整段复刻</li>
          <li><b>&gt;15 秒</b>：自动截取<b>前 15 秒</b>复刻（开头通常是爆款钩子，最值得复刻）</li>
          <li>精彩内容在<b>中后段</b>？→ 先用剪映/剪辑工具把目标段剪成 ≤15s 单独上传，再继续本流程</li>
          <li>多条分段可分别复刻、分别入发布队列，按顺序排期发布形成系列</li>
          <li>🔄 进阶预告：智能分镜自动拆段（长视频一键分段复刻）已在 V2 规划中</li>
        </ul>
      </div>
      <div v-if="sourceAsset" class="src-preview">
        <video :src="sourceAsset.url" controls preload="metadata" class="src-video" @loadedmetadata="onSrcMeta" />
        <div class="src-info">已选：{{ sourceAsset.title || sourceAsset.file_name }}（{{ sourceAsset.id }}）· 将保留其人物动作 / 镜头 / 讲解节奏</div>
        <div v-if="srcDur > 15.5" class="over15-panel">📏 检测到源视频 {{ srcDur.toFixed(1) }} 秒 &gt; 15 秒 —— 将按上方「时长规则」自动取前 15 秒复刻；如需复刻中后段请先剪辑该段再上传。</div>
      </div>
    </el-card>

    <!-- ============ ② 配置替换参考 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>第二步：配置替换参考（人 / 景 / 品）</span>
          <el-tag size="small" type="info" effect="plain">按 人物 → 背景 → 商品 顺序自动编号为 参考图1/2/3</el-tag>
        </div>
      </template>
      <div class="slot-row">
        <div v-for="(slot, k) in slots" :key="k" class="slot-col">
          <div class="slot-name">{{ slot.no }} {{ slot.title }}</div>
          <div class="slot-desc">{{ slot.desc }}</div>
          <el-upload :auto-upload="false" :show-file-list="false" accept="image/*" :on-change="f => uploadFile('image', f)" style="display:inline-block">
            <el-button size="small" plain>📤 上传</el-button>
          </el-upload>
          <el-select v-model="slot.model" filterable clearable placeholder="选素材库图片" style="width:100%" :disabled="slot.done" @change="syncSlotAssets">
            <el-option v-for="a in imageAssets" :key="a.id" :label="(a.title || a.file_name) + '  #' + a.id" :value="a.id" />
          </el-select>
          <div v-if="slot.asset" class="slot-preview">
            <img :src="slot.asset.url" class="slot-img" />
            <span class="slot-rm" @click="clearSlot(slot)">✕ 移除</span>
          </div>
          <div v-else class="slot-empty">未选（可选，但至少选一项）</div>
        </div>
      </div>
      <div class="row-tip">
        提示：选了多张参考图时，模型会按「参考图1 / 2 / 3」顺序识别用途——即 人物形象照放第一槽、背景放第二、商品放第三。
        单个替换项越清晰（正脸照 / 白底商品图），替换越准。
      </div>
    </el-card>

    <!-- ============ ③ 参数与提交 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>第三步：输出设置并生成</span>
        </div>
      </template>
      <el-form label-width="92px" label-position="left" class="out-form">
        <el-form-item label="时长">
          <el-radio-group v-model="form.duration">
            <el-radio-button v-for="d in [4, 8, 10, 12, 15]" :key="d" :value="d">{{ d }} 秒</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="清晰度">
          <el-radio-group v-model="form.resolution">
            <el-radio-button value="768P">768P（省点）</el-radio-button>
            <el-radio-button value="2K">2K（更清晰）</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="画幅">
          <el-radio-group v-model="form.ratio">
            <el-radio-button value="">跟随源视频</el-radio-button>
            <el-radio-button value="9:16">竖屏 9:16</el-radio-button>
            <el-radio-button value="16:9">横屏 16:9</el-radio-button>
            <el-radio-button value="1:1">方形</el-radio-button>
          </el-radio-group>
          <div class="row-tip">抖音/快手建议跟随源视频或 9:16</div>
        </el-form-item>
        <el-form-item label="标题/描述" label-position="top" class="full-item">
          <div class="ai-copy-bar">
            <span class="acb-tip">发布标题与文案可让 AI 代写（DeepSeek 出 3 版任选）</span>
            <el-button size="small" type="success" plain :loading="aiCopy.loading" @click="openAiCopy">🤖 AI 代写发布文案</el-button>
          </div>
          <el-input v-model="form.publish_title" placeholder="发布标题（留空自动取描述前16字）" style="margin-bottom:6px" />
          <el-input v-model="form.publish_desc" type="textarea" :rows="5" placeholder="发布描述文案（留空自动带视频描述），可多行输入查看完整内容" style="width:100%" />
        </el-form-item>
        <el-form-item label="合规确认" label-position="top" class="full-item">
          <el-checkbox v-model="form.auth_ok" class="auth-check">
            <span class="auth-label">我确认：爆款源视频与参考图均来自自有素材或已获授权，不会侵犯他人肖像/版权</span>
          </el-checkbox>
        </el-form-item>
      </el-form>
      <div class="submit-row">
        <el-button type="primary" size="large" :loading="submitting" @click="submit">🚀 生成复刻视频</el-button>
        <span class="submit-tip">MiniMax H3 后台生成约 1-3 分钟 · 完成后自动入素材库与发布队列（含对比预览）</span>
      </div>
    </el-card>

    <!-- AI 代写结果选择 -->
    <el-dialog v-model="aiCopy.show" title="🤖 AI 代写结果（任选一版填入）" width="600px">
      <div v-if="aiCopy.title" class="ai-title-box">📌 标题：{{ aiCopy.title }}</div>
      <el-radio-group v-model="aiCopy.pick" class="ai-vers">
        <div v-for="(v, i) in aiCopy.versions" :key="i" class="ai-ver" @click="aiCopy.pick = i">
          <el-radio :value="i"><span class="av-label">第 {{ i + 1 }} 版</span></el-radio>
          <div class="av-body">{{ v }}</div>
        </div>
      </el-radio-group>
      <template #footer>
        <el-button @click="aiCopy.show = false">取消</el-button>
        <el-button type="primary" @click="applyAiCopy">✅ 应用此版</el-button>
      </template>
    </el-dialog>

    <!-- ============ ④ 我的复刻任务 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>我的复刻任务</span>
          <el-button size="small" text @click="loadTasks">🔄 刷新</el-button>
        </div>
      </template>
      <div v-if="!tasks.length" class="empty">还没有任务——选好爆款与人/景/品参考后点「生成复刻视频」</div>
      <div v-for="t in tasks" :key="t.task_id" class="task-row">
        <div class="task-head">
          <el-tag size="small" :type="statusTag(t.status)" effect="plain">{{ statusText(t.status) }}</el-tag>
          <span class="task-title">{{ t.prompt || '' }}</span>
          <span class="task-meta">#{{ t.task_id }} · {{ t.duration }}s · {{ t.resolution }}</span>
        </div>
        <div v-if="t.error" class="task-err">失败原因：{{ t.error }}</div>
        <div v-if="t.status === 'done'" class="cmp-row">
          <div class="cmp-item">
            <div class="cmp-label">源视频（复刻对象）</div>
            <video :src="t.source_url" controls class="cmp-video" />
          </div>
          <div class="cmp-item">
            <div class="cmp-label">✅ 复刻成品</div>
            <video :src="t.video_url" controls class="cmp-video" />
          </div>
          <div class="cmp-actions">
            <el-button size="small" type="success" plain @click="goPublish(t)">📤 去发布队列</el-button>
            <el-button size="small" @click="downloadVideo(t)">⬇ 下载</el-button>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '@/api'
export default {
  name: 'ReplicaStudio',
  data() {
    return {
      form: {
        source_video_id: 0, duration: 8, resolution: '768P', ratio: '',
        publish_title: '', publish_desc: '', auth_ok: false,
        account_id: 0, user_id: 0
      },
      slots: [
        { key: 'person_image_id', no: '参考图1', title: '🧑 人物（形象照）', desc: '把源视频里的人换成 TA：正脸清晰照最佳（脸+着装都以此为准）', model: 0, asset: null, done: false },
        { key: 'scene_image_id',  no: '参考图2', title: '🏞️ 背景 / 场景',    desc: '把源视频环境换成该场景：门店、工厂、户外等环境图', model: 0, asset: null, done: false },
        { key: 'product_image_id', no: '参考图3', title: '📦 商品',          desc: '把源视频中展示/使用的商品换成你的：白底商品图最佳', model: 0, asset: null, done: false }
      ],
      imageAssets: [], videoAssets: [], tasks: [], submitting: false,
      srcDur: 0,
      aiCopy: { show: false, loading: false, topic: '', title: '', versions: [], pick: 0 }
    }
  },
  computed: {
    sourceAsset() { return this.videoAssets.find(a => a.id === this.form.source_video_id) || null }
  },
  created() {
    const acc = localStorage.getItem('current_account_id')
    const uid = localStorage.getItem('user_id')
    if (acc) this.form.account_id = Number(acc)
    if (uid) this.form.user_id = Number(uid)
    // 从爆款雷达「一键复刻」带入：?source_asset_id=X（素材下载完成后跳转自动预选）
    const qid = Number(this.$route.query.source_asset_id || 0)
    if (qid) this._preselect = qid
    this.loadAssets()
    this.loadTasks()
    this.$nextTick(() => { this._timer = setInterval(this.refreshAll, 6000) })
  },
  beforeDestroy() { if (this._timer) clearInterval(this._timer) },
  methods: {
    fileUrl(path) {
      if (!path) return ''
      return '/media/' + encodeURIComponent(String(path).split(/[\\/]/).pop())
    },
    statusTag(s) { return { pending: 'info', running: 'warning', done: 'success', failed: 'danger' }[s] || 'info' },
    statusText(s) { return { pending: '排队中', running: '生成中…', done: '已完成', failed: '失败' }[s] || s },
    clearSlot(slot) { slot.model = 0; slot.asset = null },
    async loadAssets() {
      try {
        const r = await api.get('/assets', { params: { limit: 100 } })
        const list = (r.assets || []).map(a => ({ ...a, file_name: String(a.file_path).split(/[\\/]/).pop(), url: this.fileUrl(a.file_path) }))
        this.imageAssets = list.filter(a => a.type === 'image')
        this.videoAssets = list.filter(a => a.type === 'video')
        this.syncSlotAssets()
        // 雷达带入：素材就绪后自动选中源视频
        if (this._preselect) {
          const hit = this.videoAssets.find(a => a.id === this._preselect)
          if (hit) {
            this.form.source_video_id = hit.id
            this.$message.success('已从爆款雷达带入源视频 ✓ 补 3 张参考图即可生成')
          } else {
            this.$message.warning('带入的源素材未找到，请手动选择')
          }
          this._preselect = 0
        }
      } catch (e) { console.error(e) }
    },
    syncSlotAssets() {
      this.slots.forEach(s => {
        s.asset = this.imageAssets.find(a => a.id === s.model) || null
      })
    },
    async uploadFile(type, fileObj) {
      const raw = fileObj.raw || fileObj
      if (!raw) return
      const fd = new FormData()
      fd.append('file', raw)
      fd.append('title', raw.name || '')
      fd.append('account_id', localStorage.getItem('current_account_id') || 0)
      try {
        const r = await api.post('/assets', fd)
        if (r && r.id) {
          this.$message.success(type === 'image' ? '图片已上传素材库' : '视频已上传素材库')
          await this.loadAssets()
          if (type === 'image') {
            // 把刚上传的图自动填到第一个未选槽
            const empty = this.slots.find(s => !s.model)
            if (empty) { empty.model = r.id }
          } else {
            this.form.source_video_id = r.id
          }
        }
      } catch (e) { this.$message.error(e.message || '上传失败') }
    },
    buildPayload() {
      const p = { account_id: this.form.account_id, user_id: this.form.user_id }
      p.source_video_id = this.form.source_video_id
      this.slots.forEach(s => { p[s.key] = s.model || 0 })
      p.duration = this.form.duration
      p.resolution = this.form.resolution
      p.ratio = this.form.ratio
      p.create_publish = true
      p.publish_title = this.form.publish_title.trim()
      p.publish_desc = this.form.publish_desc.trim()
      return p
    },
    async submit() {
      if (!this.form.source_video_id) { this.$message.warning('请先选择爆款源视频'); return }
      if (!this.slots.some(s => s.model)) { this.$message.warning('请至少选择 人物/背景/商品 一项参考图'); return }
      if (!this.form.auth_ok) { this.$message.warning('请先勾选合规确认（素材授权）'); return }
      if (!this.form.user_id) {
        this.$confirm('生成需要登录并消耗找客点，现在去登录？', '未登录', {
          confirmButtonText: '去登录', cancelButtonText: '取消', type: 'warning'
        }).then(() => this.$router.push('/login')).catch(() => {})
        return
      }
      this.submitting = true
      try {
        const p = this.buildPayload()
        const r = await api.post('/content/replica', p)
        const src = this.sourceAsset
        this.tasks.unshift({
          task_id: r.task_id, prompt: p.publish_title || '爆款复刻任务', status: 'pending',
          duration: p.duration, resolution: p.resolution,
          source_url: src ? src.url : '', video_url: '', queue_id: null, error: ''
        })
        this.saveTasks()
        this.$message.success('复刻任务已提交 ✅ 约 1-3 分钟，完成后自动入发布队列')
      } catch (e) {
        this.$message.error((e.response && e.response.data && (e.response.data.detail || e.response.data.msg)) || e.message || '提交失败')
      } finally { this.submitting = false }
    },
    onSrcMeta(e) { this.srcDur = (e.target && e.target.duration) || 0 },
    async openAiCopy() {
      if (!this.sourceAsset && !this.form.publish_title.trim()) {
        this.$message.warning('建议先选好爆款源视频或填一句主题，AI 代写更贴合内容')
      }
      try {
        const hint = (this.sourceAsset ? (this.sourceAsset.title || this.sourceAsset.file_name) : '') || this.form.publish_title.trim()
        const { value } = await this.$prompt('一句话说明这条复刻视频的内容/卖点，AI 将据此生成标题 + 3 版发布文案', '🤖 AI 代写发布文案', {
          inputPlaceholder: '例如：源头酒厂老板分享白酒知识并展示自家产品',
          confirmButtonText: '生成', cancelButtonText: '取消', inputValue: hint
        })
        if (!value || !value.trim()) { this.$message.warning('请先输入内容说明'); return }
        this.aiCopy.loading = true
        const r = await api.post('/assistant/copy-gen', { topic: value.trim(), style: '爆款带货复刻风格' })
        this.aiCopy.title = r.title || ''
        this.aiCopy.versions = (r.versions || []).slice(0, 3)
        this.aiCopy.pick = 0
        this.aiCopy.show = true
      } catch (e) {
        if (e === 'cancel' || (e && e.message && e.message.includes('cancel'))) return
        this.$message.error((e.response && e.response.data && e.response.data.detail) || '代写失败，请稍后重试')
      } finally { this.aiCopy.loading = false }
    },
    applyAiCopy() {
      const i = this.aiCopy.pick
      if (this.aiCopy.title) this.form.publish_title = this.aiCopy.title
      if (this.aiCopy.versions[i]) this.form.publish_desc = this.aiCopy.versions[i]
      this.aiCopy.show = false
      this.$message.success('✅ 标题与文案已填入（可再手动微调）')
    },
    loadTasks() {
      try { this.tasks = JSON.parse(localStorage.getItem('replica_tasks') || '[]') } catch (e) { this.tasks = [] }
    },
    saveTasks() { localStorage.setItem('replica_tasks', JSON.stringify(this.tasks)) },
    async refreshAll() {
      const pending = this.tasks.filter(t => ['pending', 'running'].includes(t.status))
      if (!pending.length) return
      for (const t of pending) {
        try {
          const r = await api.get('/content/tasks/' + t.task_id)
          t.status = r.status
          if (r.status === 'done') {
            const paths = r.result_paths || []
            if (paths.length) t.video_url = this.fileUrl(paths[0])
            if (r.result) t.queue_id = r.result.queue_id || null
            this.$message.success('复刻视频生成完成 ✅ 已自动加入发布队列')
          } else if (r.status === 'failed') {
            t.error = r.error || '生成失败'
          }
        } catch (e) { /* 单任务失败下轮重试 */ }
      }
      this.saveTasks()
    },
    async downloadVideo(t) {
      if (!t.video_url) return
      const a = document.createElement('a')
      a.href = t.video_url; a.download = '复刻_' + t.task_id + '.mp4'
      document.body.appendChild(a); a.click(); a.remove()
    },
    goPublish(t) { this.$router.push('/publish?focus=' + (t.queue_id || '')) }
  }
}
</script>

<style scoped>
.replica-studio { max-width: 1060px; margin: 0 auto; padding: 8px 16px 40px; }
.h3-hero { background: linear-gradient(120deg, #101a33 0%, #1b2a55 45%, #35206b 100%); border-radius: 12px; padding: 12px 18px; display: flex; flex-direction: column; gap: 8px; box-shadow: 0 4px 14px rgba(20, 30, 70, .22); }
.hero-brand { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.hero-logo { height: 26px; width: auto; object-fit: contain; filter: drop-shadow(0 1px 5px rgba(255,255,255,.16)); }
.hero-title .ht-main { color: #fff; font-size: 17px; font-weight: 700; letter-spacing: .4px; }
.hero-title .ht-sub { color: rgba(255,255,255,.72); font-size: 12px; margin-top: 1px; }
.hero-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.h-chip { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.22); color: #eef2ff; font-size: 11.5px; padding: 3px 10px; border-radius: 999px; backdrop-filter: blur(4px); }
.mt-16 { margin-top: 16px; }
.card-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; }
.row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.row-tip { color: #8a94a6; font-size: 12px; }
.dur-guide { margin-top: 10px; background: #d6e9fc; border: 1px solid #8dbdf0; border-left: 5px solid #2f7fd0; border-radius: 8px; padding: 8px 14px; font-size: 12.5px; color: #25466e; line-height: 1.9; }
.dur-guide ul { margin: 2px 0 0; padding-left: 18px; }
.dur-guide .dg-title { font-weight: 700; color: #0d4d92; }
.src-preview { margin-top: 12px; }
.src-video { width: 260px; border-radius: 8px; background: #000; }
.src-info { color: #606266; font-size: 12px; margin-top: 4px; }
.over15-panel { margin-top: 10px; max-width: 620px; background: #fffbea; border: 1px solid #f0e2b6; border-left: 3px solid #e6a23c; border-radius: 8px; padding: 10px 14px; font-size: 12.5px; color: #6b5a1e; line-height: 1.8; }
.over15-panel ul { margin: 4px 0 0; padding-left: 18px; }
.ai-copy-bar { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; margin-bottom: 6px; }
.acb-tip { color: #8a94a6; font-size: 12px; }
.ai-title-box { background: #f0f7ff; border: 1px solid #d6e8fb; border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; font-size: 14px; font-weight: 600; }
.ai-vers { display: flex; flex-direction: column; gap: 8px; width: 100%; }
.ai-ver { display: flex; align-items: flex-start; gap: 8px; border: 1px solid #eef0f4; border-radius: 8px; padding: 8px 10px; cursor: pointer; }
.ai-ver:hover { border-color: #409eff; }
.ai-ver .av-label { font-weight: 600; color: #409eff; margin-right: 4px; white-space: nowrap; }
.ai-ver .av-body { color: #303133; font-size: 13px; line-height: 1.6; }
.slot-row { display: flex; gap: 14px; flex-wrap: wrap; }
.slot-col { flex: 1 1 280px; border: 1px dashed #c8d0dc; border-radius: 10px; padding: 12px; min-width: 260px; }
.slot-name { font-weight: 600; font-size: 14px; margin-bottom: 2px; }
.slot-desc { color: #8a94a6; font-size: 12px; margin-bottom: 8px; }
.slot-col .el-select { margin-top: 8px; }
.slot-preview { margin-top: 8px; position: relative; }
.slot-img { width: 100%; max-height: 130px; object-fit: cover; border-radius: 8px; display: block; }
.slot-rm { position: absolute; top: 4px; right: 4px; background: rgba(0,0,0,.55); color: #fff; font-size: 12px; padding: 2px 8px; border-radius: 10px; cursor: pointer; }
.slot-empty { color: #b6bfcc; font-size: 12px; margin-top: 8px; }
.submit-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.submit-tip { color: #8a94a6; font-size: 12px; }
.empty { color: #8a94a6; text-align: center; padding: 30px 0; }
.task-row { border-bottom: 1px solid #f0f2f5; padding: 12px 0; }
.task-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.task-title { font-weight: 500; flex: 1; min-width: 160px; }
.task-meta { color: #8a94a6; font-size: 12px; }
.task-err { color: #f56c6c; font-size: 13px; margin-top: 6px; }
.cmp-row { display: flex; gap: 16px; align-items: flex-start; margin-top: 10px; flex-wrap: wrap; }
.cmp-item { flex: 1 1 300px; min-width: 280px; }
.cmp-label { font-size: 12px; color: #606266; margin-bottom: 4px; }
.cmp-video { width: 100%; max-height: 260px; border-radius: 8px; background: #000; }
.cmp-actions { display: flex; gap: 8px; }
/* ===== 深色主题适配 + 布局紧凑（科技蓝黑） ===== */
.replica-studio { overflow-x: hidden; padding: 0 16px 32px; }
.h3-hero { margin-top: 0; }
.dur-guide { background: rgba(37, 70, 150, .14) !important; border-color: rgba(96, 165, 250, .32) !important; border-left-color: #3b82f6 !important; color: #b3c4dd !important; }
.dur-guide .dg-title { color: #8ec1ff !important; }
.over15-panel { background: rgba(250, 204, 21, .07) !important; border-color: rgba(250, 204, 21, .38) !important; border-left-color: #e6b93c !important; color: #dcc47e !important; }
.ai-title-box { background: rgba(37, 99, 235, .16) !important; border-color: rgba(96, 165, 250, .35) !important; color: #cfe2ff !important; }
.ai-ver { border-color: rgba(148, 163, 184, .22) !important; }
.ai-ver:hover { border-color: #409eff !important; }
.ai-ver .av-body { color: #d9e2ef !important; }
.slot-empty { color: #64748b !important; }
.row-tip, .submit-tip, .task-meta, .src-info, .cmp-label, .empty, .acb-tip, .slot-desc { color: #8fa3bd !important; }
.replica-studio :deep(.el-form-item__label) { color: #cbd5e1; }
.replica-studio :deep(.el-radio-group) { display: flex; flex-wrap: wrap; row-gap: 6px; }
.replica-studio :deep(.el-radio-group) + .el-form-item { margin-top: 2px; }
/* 第三步输出设置：输入框满幅 + 防横向滑动 */
.out-form { width: 100%; max-width: 100%; box-sizing: border-box; }
.out-form .full-item :deep(.el-form-item__content) { width: 100%; }
.out-form .full-item :deep(.el-input), .out-form .full-item :deep(.el-textarea) { width: 100% !important; }
.out-form .full-item .ai-copy-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; width: 100%; }
.out-form :deep(.el-form-item) { margin-bottom: 14px; }
.el-card { max-width: 100%; }
.el-card.mt-16 { overflow-x: hidden; }
/* 合规确认：长文案强制换行全览（缩小字号） */
.auth-check { display: flex; align-items: flex-start; height: auto; line-height: 1.5; white-space: normal; }
.auth-check :deep(.el-checkbox__label) { white-space: normal; word-break: break-word; font-size: 12.5px; line-height: 1.6; color: #9fb0cc; padding-left: 7px; }
.auth-check :deep(.el-checkbox__input) { margin-top: 1px; }
</style>
